import React, { useCallback, useMemo, useState } from "react";
import {
  CButton,
  CCol,
  CFormCheck,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CFormText,
  CInputGroup,
  CInputGroupText,
  CRow,
  CSpinner,
  CTab,
  CTabContent,
  CTabList,
  CTabPanel,
  CTabs,
} from "@coreui/react";
import type { ApiJob, ApiMonteCarloConfig } from "@tradingdroid/frontends-types";

import { useJobPolling } from "@src/hooks/redux";
import { useToastActions } from "@src/hooks/useToasts";
import { tdApi } from "@src/services/api/tdApi";
import { getApiErrorMessage } from "@src/utils/apiError";
import { STRESS_PRESETS } from "./robustnessFormat";

interface Props {
  baselineBacktestId: number;
  strategyId: number;
  onLaunched: (job: ApiJob) => void;
}

type Mode = "permutation" | "execution_stress";

// Metrics offered as the ranking / p-value metric. All are rankable BacktestMetrics fields, so the
// backend accepts any of them; the default matches the backend default.
const RANKING_METRICS = [
  "sharpe_ratio",
  "sortino_ratio",
  "calmar_ratio",
  "profit_factor",
  "cumulative_returns_pct",
  "win_rate",
  "max_drawdown_pct",
  "total_positions",
];

export const RobustnessLaunchForm: React.FC<Props> = ({
  baselineBacktestId,
  strategyId,
  onLaunched,
}) => {
  const { showToast } = useToastActions();
  const { trackActiveJob } = useJobPolling();

  const [mode, setMode] = useState<Mode>("permutation");
  const [rankingMetric, setRankingMetric] = useState("sharpe_ratio");

  // Permutation
  const [nReplicates, setNReplicates] = useState(200);
  const [volumeMode, setVolumeMode] = useState<"permute_with_bar" | "keep_real">(
    "permute_with_bar",
  );
  const [allowLowN, setAllowLowN] = useState(false);

  // Execution stress
  const [presets, setPresets] = useState<Set<string>>(new Set(["fees_1_5x", "slippage_2x"]));
  // Pre-registered risk limit: a scenario whose drawdown breaches it is flagged fragile in the view.
  const [maxDrawdownPct, setMaxDrawdownPct] = useState(25);

  const [running, setRunning] = useState(false);

  const togglePreset = (key: string) =>
    setPresets((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const nReplicatesValid =
    Number.isInteger(nReplicates) && nReplicates >= 30 && nReplicates <= 1000;
  const lowN = nReplicatesValid && nReplicates < 100;
  const drawdownValid =
    Number.isFinite(maxDrawdownPct) && maxDrawdownPct > 0 && maxDrawdownPct <= 100;

  const canRun = useMemo(() => {
    if (running) return false;
    // Under 100 replicates has low statistical power; require the operator to acknowledge it.
    if (mode === "permutation") return nReplicatesValid && (!lowN || allowLowN);
    return presets.size > 0 && drawdownValid;
  }, [running, mode, nReplicatesValid, lowN, allowLowN, presets, drawdownValid]);

  // How many child backtests the run will spawn: 1 baseline rerun plus the replicates (permutation)
  // or the sum of the selected scenarios' replicates (execution stress). Surfaced so nobody launches
  // hundreds of runs expecting one.
  const childCount = useMemo<number | null>(() => {
    if (mode === "permutation") return nReplicatesValid ? nReplicates + 1 : null;
    const selected = STRESS_PRESETS.filter((p) => presets.has(p.key));
    if (selected.length === 0) return null;
    return 1 + selected.reduce((n, p) => n + (p.scenario.replicates ?? 1), 0);
  }, [mode, nReplicates, nReplicatesValid, presets]);

  const buildConfig = useCallback((): ApiMonteCarloConfig => {
    const base: ApiMonteCarloConfig = {
      baseline_backtest_id: baselineBacktestId,
      mode,
      ranking_metric: rankingMetric,
    };
    if (mode === "permutation") {
      return {
        ...base,
        permutation: {
          n_replicates: nReplicates,
          volume_mode: volumeMode,
          allow_low_n: lowN ? allowLowN : false,
        },
      };
    }
    return {
      ...base,
      max_acceptable_drawdown_pct: drawdownValid ? maxDrawdownPct : undefined,
      execution_stress: {
        scenarios: STRESS_PRESETS.filter((p) => presets.has(p.key)).map((p) => p.scenario),
      },
    };
  }, [
    baselineBacktestId,
    mode,
    rankingMetric,
    nReplicates,
    volumeMode,
    lowN,
    allowLowN,
    presets,
    drawdownValid,
    maxDrawdownPct,
  ]);

  const run = useCallback(async () => {
    setRunning(true);
    try {
      const job = await tdApi.createJob({
        job_type_name: "monte_carlo",
        strategy_id: strategyId,
        description: `Robustness of backtest #${baselineBacktestId}`,
        config: buildConfig(),
      });
      if (!job) throw new Error("Failed to create robustness job");
      trackActiveJob({ jobId: job.id, jobType: "monte_carlo", metadata: { baselineBacktestId } });
      showToast({ message: `Robustness run #${job.id} started`, type: "success" });
      onLaunched(job);
    } catch (err) {
      showToast({
        message: `Failed to start robustness run: ${getApiErrorMessage(err)}`,
        type: "danger",
      });
    } finally {
      setRunning(false);
    }
  }, [strategyId, baselineBacktestId, buildConfig, trackActiveJob, showToast, onLaunched]);

  return (
    <div className="RobustnessLaunchForm">
      <CTabs activeItemKey={mode} onChange={(key) => setMode(key as Mode)}>
        <CTabList variant="underline-border" className="RobustnessModeTabs mb-3">
          <CTab itemKey="permutation">Permutation test</CTab>
          <CTab itemKey="execution_stress">Execution stress</CTab>
        </CTabList>
        <CTabContent>
          <CTabPanel itemKey="permutation" className="RobustnessModePanel">
            <p className="text-body-secondary mb-3">Is the edge real, or curve-fit noise?</p>
            <CRow className="g-3 align-items-start">
              <CCol md={3}>
                <CFormLabel htmlFor="robustness-nreplicates" className="mb-1">
                  Replicates
                </CFormLabel>
                <CFormInput
                  id="robustness-nreplicates"
                  type="number"
                  min={30}
                  max={1000}
                  step={10}
                  value={nReplicates}
                  onChange={(e) => setNReplicates(Number(e.target.value))}
                  invalid={!nReplicatesValid}
                />
                <CFormText>30 to 1000. 200 screens; 1000 is the reportable bar.</CFormText>
              </CCol>
              <CCol md={3}>
                <CFormLabel htmlFor="robustness-volume" className="mb-1">
                  Volume
                </CFormLabel>
                <CFormSelect
                  id="robustness-volume"
                  value={volumeMode}
                  onChange={(e) =>
                    setVolumeMode(e.target.value as "permute_with_bar" | "keep_real")
                  }
                >
                  <option value="permute_with_bar">Permute with bar</option>
                  <option value="keep_real">Keep real</option>
                </CFormSelect>
              </CCol>
              {lowN && (
                <CCol md={6} className="pt-4">
                  <CFormCheck
                    id="robustness-allow-low-n"
                    label="Acknowledge low power (under 100 replicates)"
                    checked={allowLowN}
                    onChange={(e) => setAllowLowN(e.target.checked)}
                  />
                </CCol>
              )}
            </CRow>
          </CTabPanel>
          <CTabPanel itemKey="execution_stress" className="RobustnessModePanel">
            <p className="text-body-secondary mb-3">
              Does it survive worse fees, slippage and skipped entries?
            </p>
            <CFormLabel className="mb-2">Scenarios</CFormLabel>
            <div className="RobustnessPresets d-flex flex-column gap-2">
              {STRESS_PRESETS.map((p) => (
                <CFormCheck
                  key={p.key}
                  id={`robustness-preset-${p.key}`}
                  label={p.label}
                  checked={presets.has(p.key)}
                  onChange={() => togglePreset(p.key)}
                />
              ))}
            </div>
            <CFormText>
              Each scenario is a separate run; the combined one stresses both at once.
            </CFormText>

            <CRow className="g-3 mt-2">
              <CCol md={4}>
                <CFormLabel htmlFor="robustness-maxdd" className="mb-1">
                  Max acceptable drawdown (%)
                </CFormLabel>
                <CFormInput
                  id="robustness-maxdd"
                  type="number"
                  min={1}
                  max={100}
                  step={5}
                  value={maxDrawdownPct}
                  onChange={(e) => setMaxDrawdownPct(Number(e.target.value))}
                  invalid={!drawdownValid}
                />
                <CFormText>
                  Pre-registered risk limit. A scenario whose drawdown breaches it is flagged
                  fragile.
                </CFormText>
              </CCol>
            </CRow>
          </CTabPanel>
        </CTabContent>
      </CTabs>

      <CRow className="mt-3">
        <CCol md={4}>
          <CFormLabel htmlFor="robustness-ranking" className="mb-1">
            Ranking metric
          </CFormLabel>
          <CFormSelect
            id="robustness-ranking"
            value={rankingMetric}
            onChange={(e) => setRankingMetric(e.target.value)}
          >
            {RANKING_METRICS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </CFormSelect>
          <CFormText>Metric the p-value and comparisons are computed on.</CFormText>
        </CCol>
      </CRow>

      <CRow className="mt-4 align-items-center">
        <CCol className="d-flex align-items-center gap-3 flex-wrap">
          <CInputGroup className="RobustnessBaselineTag w-auto d-inline-flex">
            <CInputGroupText>Baseline</CInputGroupText>
            <CInputGroupText className="bg-body">backtest #{baselineBacktestId}</CInputGroupText>
          </CInputGroup>
          <CButton color="primary" onClick={run} disabled={!canRun}>
            {running ? <CSpinner size="sm" /> : "Run robustness"}
          </CButton>
          {childCount != null && (
            <span
              className={`RobustnessChildEstimate small ${
                childCount > 50 ? "text-warning" : "text-body-secondary"
              }`}
            >
              Spawns ~{childCount} child backtest{childCount === 1 ? "" : "s"}
              {childCount > 50 && ". That is a lot of runs."}
            </span>
          )}
        </CCol>
      </CRow>
    </div>
  );
};
